/**
 * RemittanceComposer
 * ------------------
 * The payment-confirmation email the supplier gets. Pre-filled from the
 * payment record (institution, account ending, total, method, date, and every
 * covered invoice with its own amount). Two ways out: send it from the portal,
 * or write it into Outlook Drafts with the wire confirmation attached.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Mail, Send, ExternalLink, X, Paperclip } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { API } from "@/App";

export const RemittanceComposer = ({ open, onClose, token, payment, onSent }) => {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [subject, setSubject] = useState("");
  const [bodyHtml, setBodyHtml] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [picked, setPicked] = useState([]);

  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    if (!payment?.id) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/payables/payments/${payment.id}/remittance`, { headers: { Authorization: `Bearer ${token}` } });
      setTo((r.data.to || []).join(", "));
      setCc((r.data.cc || []).join(", "));
      setSubject(r.data.subject || "");
      setBodyHtml(r.data.body_html || "");
      setAttachments(r.data.attachments || []);
      setPicked((r.data.attachments || []).map((a) => a.id));
    } catch {
      toast.error("Could not build the remittance advice");
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payment?.id, token]);

  useEffect(() => { if (open) load(); }, [open, load]);

  if (!open) return null;

  const split = (s) => s.split(/[,;]/).map((x) => x.trim()).filter(Boolean);

  const body = () => ({
    to: split(to), cc: split(cc), subject, body_html: bodyHtml, attachment_ids: picked,
  });

  const sendFromPortal = async () => {
    if (!split(to).length) { toast.error("Add at least one recipient"); return; }
    setBusy("send");
    try {
      const r = await axios.post(`${API}/payables/payments/${payment.id}/send-remittance`, body(), { headers });
      if (r.data.success) { toast.success(`Remittance sent to ${r.data.sent_to.join(", ")}`); onSent?.(); onClose(); }
      else toast.error(r.data.detail || `Mail server returned ${r.data.email_status}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send the remittance");
    } finally { setBusy(""); }
  };

  const openOutlook = async () => {
    setBusy("outlook");
    try {
      const r = await axios.post(`${API}/payables/payments/${payment.id}/outlook-draft`, body(), { headers });
      if (r.data.web_link) { window.open(r.data.web_link, "_blank", "noopener"); toast.success("Outlook draft created"); onSent?.(); }
    } catch (e) {
      const detail = e?.response?.data?.detail || "Outlook drafting is unavailable";
      toast.error(detail);
      // Fall back to a mailto so the message can still be composed by hand.
      const plain = bodyHtml.replace(/<[^>]+>/g, "").replace(/\n{3,}/g, "\n\n");
      window.location.href = `mailto:${split(to).join(",")}?cc=${split(cc).join(",")}&subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(plain)}`;
    } finally { setBusy(""); }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-3xl max-h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()} data-testid="remittance-composer">
        <div className="px-5 py-4 border-b border-slate-200 flex items-start justify-between">
          <div>
            <h3 className="text-lg font-black text-slate-900 inline-flex items-center gap-2">
              <Mail className="w-5 h-5 text-blue-600" /> Payment confirmation &amp; remittance
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              {payment?.supplier_name} · {payment?.invoice_number}
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>

        {loading ? (
          <p className="p-8 text-slate-500 inline-flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Building the remittance…</p>
        ) : (
          <div className="p-5 overflow-y-auto space-y-3">
            <Row label="To">
              <Input value={to} onChange={(e) => setTo(e.target.value)} className="h-9 text-sm"
                placeholder="ap@supplier.com" data-testid="remittance-to" />
            </Row>
            <Row label="Cc">
              <Input value={cc} onChange={(e) => setCc(e.target.value)} className="h-9 text-sm"
                placeholder="accounting@sterilemate.com" data-testid="remittance-cc" />
            </Row>
            <Row label="Subject">
              <Input value={subject} onChange={(e) => setSubject(e.target.value)}
                className="h-9 text-sm" data-testid="remittance-subject" />
            </Row>

            {attachments.length > 0 && (
              <div className="border border-slate-200 rounded-lg p-3">
                <p className="text-[10px] font-black uppercase tracking-wider text-slate-500 mb-2 inline-flex items-center gap-1">
                  <Paperclip className="w-3 h-3" /> Attach with the email
                </p>
                {attachments.map((a) => (
                  <label key={a.id} className="flex items-center gap-2 text-sm text-slate-700 py-0.5">
                    <input type="checkbox" checked={picked.includes(a.id)} className="w-4 h-4 accent-blue-600"
                      onChange={() => setPicked((p) => p.includes(a.id) ? p.filter((x) => x !== a.id) : [...p, a.id])} />
                    {a.name}
                  </label>
                ))}
              </div>
            )}

            <div>
              <p className="text-[10px] font-black uppercase tracking-wider text-slate-500 mb-1">Preview</p>
              <div className="border border-slate-200 rounded-lg p-4 bg-slate-50 max-h-[36vh] overflow-y-auto text-sm"
                data-testid="remittance-preview" dangerouslySetInnerHTML={{ __html: bodyHtml }} />
            </div>
          </div>
        )}

        <div className="px-5 py-4 border-t border-slate-200 flex flex-wrap items-center justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="outline" onClick={openOutlook} disabled={!!busy}
            className="border-blue-300 text-blue-700" data-testid="remittance-outlook">
            {busy === "outlook" ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <ExternalLink className="w-4 h-4 mr-1" />}
            Open in Outlook
          </Button>
          <Button onClick={sendFromPortal} disabled={!!busy}
            className="bg-blue-600 hover:bg-blue-700" data-testid="remittance-send">
            {busy === "send" ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
            Send from portal
          </Button>
        </div>
      </div>
    </div>
  );
};

const Row = ({ label, children }) => (
  <div className="flex items-center gap-3">
    <span className="w-16 text-[10px] font-black uppercase tracking-wider text-slate-500">{label}</span>
    <div className="flex-1">{children}</div>
  </div>
);

export default RemittanceComposer;
